import { createContext, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatchApp } from "../lib/redux";
import { getIsBlockedActions } from "../actions/getters";
import { startSaveOnboardingChecklistSkipGuide } from "../actions/adsCampaigns";
import { useCreditActionCosts } from "../hooks/useCreditActionCosts";
import { useOnboardingChecklistData } from "../hooks/useOnboardingChecklistData";
import { triggerClick } from "../utils/triggerClick";
import ShareModalMultiple from "../components/Gallery/ShareModalMultiple/ShareModalMultiple";
import {
  ONBOARDING_CHECKLIST_ACTION_KEYS,
  ONBOARDING_CHECKLIST_STEPS,
} from "../data/onboardingChecklistSteps";

export const OnboardingChecklistContext = createContext(null);

export const OnboardingChecklistProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION } = useCreditActionCosts();
  const { brandSetup, creatives, posts, campaigns, skipGuide, isReady } =
    useOnboardingChecklistData();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const flagsByKey = useMemo(
    () => ({
      brandSetup,
      creatives,
      posts,
      campaigns,
    }),
    [brandSetup, creatives, posts, campaigns]
  );

  const onOpenGallery = useCallback(() => {
    triggerClick({ id: "button-open-gallery", delay: 100 });
    triggerClick({ id: "button-create-with-ia-modal-gallery", delay: 400 });
  }, []);

  const onOpenShareModal = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  const onCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  const onNavigateToSettings = useCallback(async () => {
    navigate("/settings");
  }, [navigate]);

  const onNavigateToCampaigns = useCallback(async () => {
    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
        showUsageConfirmationAlert: false,
      })
    );
    if (!isBlocked) {
      navigate("/ads-campaign");
    }
  }, [ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION, dispatch, navigate]);

  const actionsByKey = useMemo(
    () => ({
      [ONBOARDING_CHECKLIST_ACTION_KEYS.BRAND_SETUP]: onNavigateToSettings,
      [ONBOARDING_CHECKLIST_ACTION_KEYS.CREATIVES]: onOpenGallery,
      [ONBOARDING_CHECKLIST_ACTION_KEYS.PUBLISH]: onOpenShareModal,
      [ONBOARDING_CHECKLIST_ACTION_KEYS.CAMPAIGN]: onNavigateToCampaigns,
    }),
    [
      onNavigateToCampaigns,
      onNavigateToSettings,
      onOpenGallery,
      onOpenShareModal,
    ]
  );

  const items = useMemo(
    () =>
      ONBOARDING_CHECKLIST_STEPS.map((step) => ({
        ...step,
        completed: Boolean(flagsByKey[step.flagKey]),
      })),
    [flagsByKey]
  );

  const completedCount = useMemo(
    () => items.filter((item) => item.completed).length,
    [items]
  );

  const totalItems = items.length;

  const progress = useMemo(
    () => (totalItems > 0 ? (completedCount / totalItems) * 100 : 0),
    [completedCount, totalItems]
  );

  const allCompleted = brandSetup && creatives && posts && campaigns;
  const showOnboardingChecklist = isReady && !allCompleted && !skipGuide;

  const onSkipGuide = useCallback(async () => {
    await dispatch(startSaveOnboardingChecklistSkipGuide());
  }, [dispatch]);

  const onActionStep = useCallback(
    async ({ actionKey }) => {
      const action = actionsByKey[actionKey];
      if (!action) {
        return;
      }

      await action();
    },
    [actionsByKey]
  );

  const value = useMemo(
    () => ({
      items,
      completedCount,
      totalItems,
      progress,
      allCompleted,
      isReady,
      showOnboardingChecklist,
      isShareModalOpen,
      onCloseShareModal,
      onActionStep,
      onSkipGuide,
    }),
    [
      allCompleted,
      completedCount,
      isReady,
      isShareModalOpen,
      items,
      onActionStep,
      onCloseShareModal,
      onSkipGuide,
      progress,
      showOnboardingChecklist,
      totalItems,
    ]
  );

  return (
    <OnboardingChecklistContext.Provider value={value}>
      {children}
      {isShareModalOpen ? (
        <ShareModalMultiple
          modalOpen={isShareModalOpen}
          onCloseModal={onCloseShareModal}
          item={null}
        />
      ) : null}
    </OnboardingChecklistContext.Provider>
  );
};
